import { useState, useEffect, type ReactNode } from 'react';
import { StoreProvider, type StoreSettings } from '@lib/storeContext';
import { AuthProvider } from '@lib/authContext';
import { CartProvider } from '@lib/cartContext';
import { loadMarketplaceConfig, type MarketplaceConfig } from '@lib/marketplaceConfig';

/**
 * Client-side replacement for the Next.js server-component StoreLayoutWrapper.
 * Loads marketplace config from Firestore on mount, then provides
 * StoreProvider + CartProvider to the component tree.
 */
export function MarketplaceStoreProvider({ children }: { children: ReactNode }) {
  const [storeSettings, setStoreSettings] = useState<StoreSettings | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadConfig() {
      try {
        const config: MarketplaceConfig | null = await loadMarketplaceConfig();
        const currentUrl = window.location.origin;

        const settings: StoreSettings = {
          id: config?.marketplaceId || 'default-marketplace',
          organizationId: config?.organizationId || '',
          distributorId: config?.distributorId || config?.organizationId || '',
          displayName: config?.name || 'Marketplace',
          themeColor: config?.themeColor || '#105c38',
          bannerImage: config?.logo || undefined,
          baseUrl: config?.domain || currentUrl,
          isPublic: config?.public ?? true,
          announcementBar: config?.headerBarText || undefined,
          featuredCategories: config?.featuredCategories || undefined,
          deliveryWindows: undefined,
          contactEmail: config?.contactEmail || undefined,
          contactPhone: config?.contactPhone || undefined,
          showRecipes: config?.showRecipes ?? true,
          showDeliverySchedule: config?.showDeliverySchedule ?? true,
          showBuyAgain: config?.showBuyAgain ?? false,
          showReturns: config?.showReturns ?? false,
          showSupport: config?.showSupport ?? false,
          showChat: config?.showChat ?? false,
        };

        setStoreSettings(settings);
      } catch {
        // Fallback settings when config fails to load
        setStoreSettings({
          id: 'default-marketplace',
          organizationId: '',
          distributorId: '',
          displayName: 'Marketplace',
          themeColor: '#105c38',
          bannerImage: undefined,
          baseUrl: window.location.origin,
          isPublic: true,
          announcementBar: undefined,
          featuredCategories: undefined,
          deliveryWindows: undefined,
          contactEmail: undefined,
          contactPhone: undefined,
          showRecipes: true,
          showDeliverySchedule: true,
          showBuyAgain: false,
          showReturns: false,
          showSupport: false,
          showChat: false,
        });
      } finally {
        setLoading(false);
      }
    }

    loadConfig();
  }, []);

  if (loading || !storeSettings) {
    return (
      <div className="min-h-screen bg-[#f9fafb] flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          <p className="mt-4 text-slate-600">Loading store...</p>
        </div>
      </div>
    );
  }

  return (
    <StoreProvider settings={storeSettings}>
      <AuthProvider>
        <CartProvider storeId={storeSettings.id}>
          {children}
        </CartProvider>
      </AuthProvider>
    </StoreProvider>
  );
}
